import { useMutation } from '@tanstack/react-query'
import { isProblemError } from '@volee/api-client'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  applyProblems,
  resetConfirmProblems,
  resetRequestProblems,
  type ResetConfirmFields,
  type ResetRequestFields,
} from './auth-fields.ts'
import { passwordRecoveryWriter } from './password-recovery.ts'
import { isUsername } from './sign-in-identifier.ts'
import { useSessionLanding } from './use-sign-in.ts'
import { errorMessage, messages } from '../messages/index.ts'
import { auth } from '../services/auth.ts'

// The two steps of /forgot-password. Both are re-enterable by construction, which is why
// this one IS a route while the first access is a state of /sign-in.

interface Confirmation {
  readonly identifier: string
  readonly fields: ResetConfirmFields
}

/** Where a refusal of the confirmation belongs; absent, it goes above the form. */
function confirmFieldOf(error: unknown): 'code' | 'newPassword' | undefined {
  if (!isProblemError(error)) return undefined
  if (error.code === 'verification_code.invalid') return 'code'
  if (error.code === 'account.password_rejected') return 'newPassword'
  return undefined
}

export function usePasswordReset(returnTo: string) {
  const land = useSessionLanding()
  // What the request was for, kept: a mail moves on to the code, a username to the tutori told.
  const [askedFor, setAskedFor] = useState<string | null>(null)
  const requestForm = useForm<ResetRequestFields>({ defaultValues: { email: '' } })
  const confirmForm = useForm<ResetConfirmFields>({
    defaultValues: { code: '', newPassword: '', confirmPassword: '' },
  })

  // The answer is the same for an unknown address and a known one: the page is no oracle.
  const request = useMutation({
    mutationFn: (identifier: string) => passwordRecoveryWriter.request(identifier),
    onSuccess: (_result, identifier) => setAskedFor(identifier),
  })

  // Two mutations and not one: past the first the password IS changed, so a failure of the
  // second is a different story — and reported as one, or the user retries a spent code.
  const confirm = useMutation({
    mutationFn: ({ identifier, fields }: Confirmation) =>
      passwordRecoveryWriter.confirm({
        identifier,
        code: fields.code.trim(),
        password: fields.newPassword,
      }),
    onSuccess: (_result, confirmation) => signIn.mutate(confirmation),
    onError: (error: unknown) => {
      const field = confirmFieldOf(error)
      if (field !== undefined) confirmForm.setError(field, { message: errorMessage(error) })
    },
  })

  // Setting a password does not authenticate, so one path establishes a session.
  const signIn = useMutation({
    mutationFn: ({ identifier, fields }: Confirmation) =>
      auth.password.signIn({ email: identifier, password: fields.newPassword }),
    onSuccess: () => land(returnTo),
    // No input to blame — the new password works, it is this sign-in that did not. The page
    // says so and offers the login, instead of marking a code already spent.
    onError: () => {},
  })

  const codeFor = askedFor !== null && !isUsername(askedFor) ? askedFor : null

  function submitRequest(fields: ResetRequestFields) {
    requestForm.clearErrors()
    const problems = resetRequestProblems(fields, messages.password.required)
    if (applyProblems(requestForm.setError, problems)) return
    const typed = fields.email.trim()
    request.mutate(isUsername(typed) ? typed.toLowerCase() : typed)
  }

  function submitConfirm(fields: ResetConfirmFields) {
    confirmForm.clearErrors()
    if (applyProblems(confirmForm.setError, resetConfirmProblems(fields, messages.password))) return
    if (codeFor !== null) confirm.mutate({ identifier: codeFor, fields })
  }

  return {
    /** The mail the code was asked for, null until then: it is what selects the second step. */
    codeFor,
    /** The username whose tutori were told, once they were: the page's last word on it. */
    toldFor: askedFor !== null && isUsername(askedFor) ? askedFor : null,
    request: {
      form: requestForm,
      submit: submitRequest,
      pending: request.isPending,
      formError: request.error === null ? undefined : errorMessage(request.error),
    },
    confirm: {
      form: confirmForm,
      submit: submitConfirm,
      pending: confirm.isPending || signIn.isPending,
      formError:
        confirm.error !== null && confirmFieldOf(confirm.error) === undefined
          ? errorMessage(confirm.error)
          : undefined,
    },
    /** True once the password is changed, whether or not the sign-in that follows worked. */
    changed: confirm.isSuccess,
  }
}
